// ============================================================================
// Feature 003 — Phase 8: Stage 4 Multi-Photo Phrasing UI Component
// ============================================================================
// Render badge "X dari Y foto kandidat cocok dengan galeri Datacenter (≥0.85)"
// per PRD § 4.4a.
//
// Single-photo confidence dilarang — wajib show count agar operator paham
// kekuatan evidence.
// ============================================================================

import React from 'react';
import { ShieldCheck, ShieldAlert, ShieldX, Image as ImageIcon } from 'lucide-react';

const BAND_STYLES = {
  high:       { color: 'var(--color-green)', bg: 'rgba(0,255,136,0.10)', border: 'rgba(0,255,136,0.40)', icon: ShieldCheck },
  medium:     { color: 'var(--color-yellow)',            bg: 'rgba(255,214,10,0.10)', border: 'rgba(255,214,10,0.40)', icon: ShieldCheck },
  low:        { color: 'var(--color-orange)',            bg: 'rgba(255,107,43,0.10)', border: 'rgba(255,107,43,0.40)', icon: ShieldAlert },
  unverified: { color: 'var(--text-mute)',   bg: 'transparent',           border: 'var(--border-dim)',     icon: ShieldX     },
};

const BAND_LABEL = {
  id: { high: 'Verifikasi Wajah Tinggi', medium: 'Verifikasi Wajah Sedang', low: 'Verifikasi Wajah Lemah', unverified: 'Tidak Diverifikasi' },
  en: { high: 'High Face Verification',  medium: 'Medium Face Verification', low: 'Low Face Verification',  unverified: 'Unverified'         },
};

/**
 * Render badge Stage 4 Joint Confidence.
 *
 * @param {object} props
 * @param {number} props.matchCount   Jumlah foto cocok ≥ threshold
 * @param {number} props.totalCount   Jumlah foto kandidat valid
 * @param {number} [props.threshold]  Default 0.85
 * @param {string} [props.band]       'high' | 'medium' | 'low' | 'unverified'
 * @param {string} [props.lang]       'id' | 'en'
 * @param {string} [props.explanation] Override phrasing (jika sudah dipersist di DB)
 */
export default function PhotoMatchExplanation({
  matchCount = 0,
  totalCount = 0,
  threshold = 0.85,
  band,
  lang = 'id',
  explanation,
}) {
  // Auto-classify band kalau tidak provided
  const computedBand = band || classifyBand(matchCount, totalCount);
  const style = BAND_STYLES[computedBand] || BAND_STYLES.unverified;
  const Icon = style.icon;
  const label = BAND_LABEL[lang === 'en' ? 'en' : 'id'][computedBand];

  const phrase = explanation || formatPhrase(matchCount, totalCount, threshold, lang);

  return (
    <div
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '6px 10px', borderRadius: 8,
        background: style.bg, border: `1px solid ${style.border}`,
      }}
      role="status"
      aria-label={`${label} — ${phrase}`}
    >
      <Icon size={14} color={style.color} strokeWidth={2.5} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <span style={{
          fontSize: 9, fontFamily: 'IBM Plex Mono', fontWeight: 700,
          color: style.color, textTransform: 'uppercase', letterSpacing: 0.5,
        }}>
          {label}
        </span>
        <span style={{
          fontSize: 10, fontFamily: 'IBM Plex Mono',
          color: 'var(--text-hi)',
        }}>
          {phrase}
        </span>
      </div>
    </div>
  );
}

function classifyBand(matchCount, totalCount) {
  if (totalCount < 1) return 'unverified';
  if (matchCount >= 3 && totalCount >= 4) return 'high';
  if (matchCount >= 2) return 'medium';
  if (matchCount >= 1) return 'low';
  return 'unverified';
}

function formatPhrase(matchCount, totalCount, threshold, lang) {
  if (totalCount < 1) {
    return lang === 'en'
      ? 'Photos could not be verified'
      : 'Foto tidak dapat diverifikasi';
  }
  const thr = threshold.toFixed(2);
  return lang === 'en'
    ? `${matchCount} of ${totalCount} candidate photos match Datacenter (≥ ${thr})`
    : `${matchCount} dari ${totalCount} foto kandidat cocok dengan Datacenter (≥ ${thr})`;
}
